import { newId } from '@quizee/events/id';
import { DEVICE_KEY, readLocal, votedKey, writeLocal } from './storage';

/**
 * Progressive identity, step one.
 *
 * The session id is always minted and never leaves memory. The device id only exists when the
 * reader has consented to storage, because the event schema rejects an event that carries one
 * without that consent, and rightly so.
 */
export interface Identity {
  readonly session_id: string;
  readonly device_id?: string | undefined;
}

const DEVICE_ID = /^dev_[0-9a-f]{32}$/;
const OPTION_ID = /^opt_[0-9a-f]{32}$/;

export function resolveIdentity(storageAllowed: boolean): Identity {
  const session_id = newId('session');
  if (!storageAllowed) return { session_id };

  const stored = readLocal(DEVICE_KEY);
  if (stored !== null && DEVICE_ID.test(stored)) {
    return { session_id, device_id: stored };
  }

  const device_id = newId('device');
  writeLocal(DEVICE_KEY, device_id);
  return { session_id, device_id };
}

/**
 * A remembered vote is what stops a returning reader being asked the same question twice. Without
 * storage consent there is nowhere to remember it, so they simply get to vote again.
 */
export function readVote(interactionId: string, storageAllowed: boolean): string | null {
  if (!storageAllowed) return null;
  const stored = readLocal(votedKey(interactionId));
  return stored !== null && OPTION_ID.test(stored) ? stored : null;
}

export function rememberVote(
  interactionId: string,
  optionId: string,
  storageAllowed: boolean,
): void {
  if (!storageAllowed) return;
  writeLocal(votedKey(interactionId), optionId);
}
